
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SchoolInfoForm } from "@/components/auth/SchoolInfoForm";
import { PositionSelect } from "@/components/auth/PositionSelect";
import { useToast } from "@/components/ui/use-toast";
import { createLogger } from "@/lib/utils/logger";

const logger = createLogger('auth', 'ProfileCompletion');

interface ProfileCompletionProps {
  userId: string;
}

export const ProfileCompletion = ({ userId }: ProfileCompletionProps) => {
  // School info state
  const [schoolDistrict, setSchoolDistrict] = useState("");
  const [school, setSchool] = useState("");
  const [customSchoolDistrict, setCustomSchoolDistrict] = useState("");
  const [customSchool, setCustomSchool] = useState("");
  
  // Position state
  const [position, setPosition] = useState("");
  const [customPosition, setCustomPosition] = useState("");
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  
  // Data state
  const [districts, setDistricts] = useState<{ value: string; label: string }[]>([]);
  const [schools, setSchools] = useState<{ value: string; label: string }[]>([]);
  const [positions, setPositions] = useState<{ value: string; label: string }[]>([]);
  
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch positions
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        logger.info('Starting positions fetch...');
        
        const { data, error } = await supabase
          .from('positions')
          .select('title, category')
          .order('title');
        
        if (error) {
          logger.error('Supabase error fetching positions: ' + error.message);
          toast({
            title: "Error",
            description: "Failed to load positions. Please try again.",
            variant: "destructive",
          });
          return;
        }
        
        if (Array.isArray(data)) {
          // Format positions as simple title values for the dropdown
          const formattedPositions = data.map(pos => ({
            value: pos.title,
            label: pos.title,
          }));
          
          // Add "Other" option if not present
          if (!formattedPositions.some(p => p.value === "Other")) {
            formattedPositions.push({ value: "Other", label: "Other (specify)" });
          }
          
          // Remove duplicates by title
          const uniquePositions = Array.from(
            new Map(formattedPositions.map(item => [item.value, item])).values()
          );
          
          logger.info('Positions loaded: ' + JSON.stringify({ count: uniquePositions.length }));
          setPositions(uniquePositions);
        } else {
          logger.error('Positions data is not an array: ' + typeof data);
          toast({
            title: "Error",
            description: "Received invalid positions data format",
            variant: "destructive",
          });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Exception in fetchPositions: ' + errorMessage);
        toast({
          title: "Error",
          description: "Failed to load positions. Please try again.",
          variant: "destructive",
        });
      }
    };
    
    fetchPositions();
  }, [toast]);

  // Fetch school districts
  useEffect(() => {
    const fetchDistricts = async () => {
      setIsLoadingDistricts(true);
      try {
        logger.info('Fetching districts...');
        const { data, error } = await supabase
          .from('school_districts')
          .select('id, name')
          .order('name');

        if (error) {
          logger.error('Error fetching districts: ' + error.message);
          toast({
            title: "Error",
            description: "Failed to load school districts. Please try again.",
            variant: "destructive",
          });
          return;
        }

        if (data) {
          const formattedDistricts = data.map(district => ({
            value: district.name,
            label: district.name,
          }));
          
          // Add "Other" option if not present
          if (!formattedDistricts.some(d => d.value === "Other")) {
            formattedDistricts.push({ value: "Other", label: "Other (specify)" });
          }
          
          logger.info('Districts loaded: ' + JSON.stringify({ count: formattedDistricts.length }));
          setDistricts(formattedDistricts);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Error fetching districts: ' + errorMessage);
        toast({
          title: "Error",
          description: "Failed to load school districts. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingDistricts(false);
      }
    };
    fetchDistricts();
  }, [toast]);

  // Fetch schools when district changes
  useEffect(() => {
    const fetchSchools = async () => {
      if (!schoolDistrict || schoolDistrict === "Other") {
        setSchools([]);
        return;
      }

      try {
        logger.info('Fetching schools for district: ' + schoolDistrict);
        
        // First get the district ID
        const { data: districtData, error: districtError } = await supabase
          .from('school_districts')
          .select('id')
          .eq('name', schoolDistrict)
          .maybeSingle();

        if (districtError) {
          logger.error('Error fetching district ID: ' + districtError.message);
          return;
        }

        if (!districtData) {
          logger.warning('No district found with name: ' + schoolDistrict);
          setSchools([]);
          return;
        }

        // Then get the schools for that district
        const { data: schoolsData, error: schoolsError } = await supabase
          .from('schools')
          .select('id, name')
          .eq('district_id', districtData.id)
          .order('name');

        if (schoolsError) {
          logger.error('Error fetching schools: ' + schoolsError.message);
          toast({
            title: "Error",
            description: "Failed to load schools. Please try again.",
            variant: "destructive",
          });
          return;
        }

        if (schoolsData && schoolsData.length > 0) {
          const formattedSchools = schoolsData.map(school => ({
            value: school.name,
            label: school.name,
          }));
          
          // Add "Other" option if not present
          if (!formattedSchools.some(s => s.value === "Other")) {
            formattedSchools.push({ value: "Other", label: "Other (specify)" });
          }
          
          logger.info('Schools loaded: ' + JSON.stringify({ count: formattedSchools.length, district: schoolDistrict }));
          setSchools(formattedSchools);
        } else {
          // If no schools found for district, just provide the "Other" option
          logger.info('No schools found for district: ' + schoolDistrict);
          setSchools([{ value: "Other", label: "Other (specify)" }]);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error('Error fetching schools: ' + errorMessage);
        toast({
          title: "Error",
          description: "Failed to load schools. Please try again.",
          variant: "destructive",
        });
      }
    };
    fetchSchools();
  }, [schoolDistrict, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsLoading(true);
    setErrorMessage("");

    // Validate district selection
    if (!schoolDistrict || (schoolDistrict === "Other" && !customSchoolDistrict)) {
      setErrorMessage("Please select or specify a school district.");
      setIsLoading(false);
      return;
    }

    // Validate school selection
    if (!school || (school === "Other" && !customSchool)) {
      setErrorMessage("Please select or specify a school.");
      setIsLoading(false);
      return;
    }

    // Validate position selection
    if (!position || (position === "Other" && !customPosition)) {
      setErrorMessage("Please select or specify a position.");
      setIsLoading(false);
      return;
    }

    try {
      // Use custom values when "Other" is selected
      const finalSchoolDistrict = schoolDistrict === "Other" ? customSchoolDistrict : schoolDistrict;
      const finalSchool = school === "Other" ? customSchool : school;
      const finalPosition = position === "Other" ? customPosition : position;

      logger.info('Updating profile with: ' + JSON.stringify({ 
        district: finalSchoolDistrict, 
        school: finalSchool, 
        position: finalPosition 
      }));

      // Update the user's profile
      const { error } = await supabase
        .from('profiles')
        .update({
          school_district: finalSchoolDistrict,
          school: finalSchool,
          position: finalPosition,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      logger.info('Profile completed successfully for user: ' + userId);
      
      toast({
        title: "Profile completed",
        description: "Your profile has been successfully completed.",
      });
      
      // Redirect to dashboard
      navigate("/");
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : "Failed to update profile. Please try again.";
      
      logger.error('Profile completion error: ' + errorMessage);
      setErrorMessage(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="backdrop-blur-sm border-primary/20">
      <CardHeader className="space-y-1">
        <div className="flex flex-col items-center mb-4">
          <img 
            src="/lovable-uploads/cc742b1a-3eb0-4c19-b31f-8e524aa87815.png"
            alt="NWSISD Logo"
            className="h-24 w-auto mb-4"
          />
        </div>
        <CardTitle className="text-2xl text-center font-title text-primary">Complete Your Profile</CardTitle>
        <CardDescription className="text-center font-sans">
          Please provide a few more details to complete your profile
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {errorMessage && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          
          <SchoolInfoForm
            schoolDistrict={schoolDistrict}
            school={school}
            customSchoolDistrict={customSchoolDistrict}
            customSchool={customSchool}
            onSchoolDistrictChange={setSchoolDistrict}
            onSchoolChange={setSchool}
            onCustomSchoolDistrictChange={setCustomSchoolDistrict}
            onCustomSchoolChange={setCustomSchool}
            districts={districts}
            schools={schools}
            isLoadingDistricts={isLoadingDistricts}
          />
          
          <PositionSelect
            position={position}
            onChange={setPosition}
            customPosition={customPosition}
            onCustomPositionChange={setCustomPosition}
            positions={positions}
          />
        </CardContent>
        <CardFooter>
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span className="font-serif">Updating profile...</span>
              </>
            ) : (
              <span className="font-serif">Complete Profile</span>
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};
